<template>
    <div>
		<missing-info v-if="!commentsCount" :text="$t('missing.comment.index')" class="min-h-160"/>
		<template v-else>
			<data-loader :url="url"
						 as-guest
						 :params="{ts: cacheBuster}"
						 v-model:data="comments">

				<template #loading>
					<comment-skeleton v-for="commentLoader in Math.min(commentsCount, 10)" tight :key="'comments-loader-' + commentLoader"/>
				</template>

				<template v-slot="{data}">
					<comment v-for="comment in data" :key="'comment-' + comment.id" :comment="comment"
								  tight
								  :liked="likedCommentIds.includes(comment.id)"
								  :update-url="`${props.url}/${comment.id}`"
								  @update:liked="handleLikeToggle(comment)"
								  @edit="editComment(comment)"
								  @destroyed="handleDestroyedComment(comment)"/>
				</template>

				<template #missing>
					<missing-info :text="$t('missing.comment.index')" class="min-h-200"/>
				</template>
			</data-loader>

			<lazy-ajax-form-modal v-if="selectedComment" v-model="isEditModalVisible"
								  :form-options="{action: `${props.url}/${selectedComment.id}`, method: 'PUT', data: editData}"
								  :modal-options="{title: $t('action.edit'), highestOrder: true}"
								  @success="data => handleUpdatedComment(selectedComment, data)">

				<lazy-form-textarea name="text"
									:rows="3"
									hide-details
									:label="$t('label.text')"
									v-model="editData.text"/>

			</lazy-ajax-form-modal>
		</template>
	</div>
</template>

<script setup lang="ts">
import {watch, ref, nextTick, onMounted} from "vue";
import type {Ref} from "vue";
import {useCommentable, useNuxtApp} from "#imports";
import type {Comment} from "@spoferan/spoferan-ts-core";

const props = withDefaults(defineProps<{
	// The URL from which to load the comments.
	// Also defines the comment updated and destroy URL.
	url: string,

	commentsCount: number,

	// The loaded comments to keep in sync
	comments?: Comment[],

	// A cache buster to force reloading the comments.
	// Should be the 'updated_at' timestamp of the owner
	cacheBuster?: number,
}>(), {
});

const {$apiFetch, $auth} = useNuxtApp();

const emit = defineEmits(['toggle-like', 'update:comments', 'update:commentsCount']);

const {comments, commentsCount, handleUpdatedComment, handleDestroyedComment} = useCommentable(props.commentsCount, props.comments);

watch(() => props.comments, (newValue: Comment[]) => {
	comments.value = newValue;
});
watch(comments, (newValue) => {
	emit('update:comments', newValue);
});
watch(() => props.commentsCount, (newValue: number) => {
	commentsCount.value = newValue;
});
watch(commentsCount, (newValue) => {
	emit('update:commentsCount', newValue);
});

const selectedComment: Ref<Comment|null> = ref(null);
const likedCommentIds = ref([]);
const isEditModalVisible = ref(false);
const editData = ref({});

function handleLikeToggle() {
	emit('toggle-like');
}

function editComment(comment: Comment) {
	selectedComment.value = comment;
	editData.value = {
		text: comment.text
	};
	nextTick(() => {
		isEditModalVisible.value = true;
	});
}

async function refreshLikedComments() {
	if (props.commentsCount && $auth.loggedIn) {
		const {data} = await $apiFetch(`${props.url}/auth-likes`);
		likedCommentIds.value = data.ids;
	} else {
		likedCommentIds.value = [];
	}
}

onMounted(() => {
	refreshLikedComments();
});

</script>